import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #section;
  #basePriceHTML = '';
  #basePriceValue = '';
  #baseHasYmqPriceHook = false;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#captureBasePrice();
    queueMicrotask(() => this.#schedulePriceForQuantityUpdate());

    if (!this.#section) return;

    this.#section.addEventListener(ThemeEvents.variantUpdate, this.updatePrice, {
      signal: this.#abortController.signal,
    });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
      this.#captureBasePrice();
      this.#schedulePriceForQuantityUpdate();
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };

  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.#isRelatedQuantityEvent(event.target)) return;

    this.#schedulePriceForQuantityUpdate(event.detail?.quantity);
  };

  #isRelatedQuantityEvent(target) {
    if (this.#section?.contains(target)) return true;

    const form = target instanceof Element ? target.closest('product-form-component') : target.parentElement?.closest('product-form-component');
    return Boolean(form && form instanceof HTMLElement && form.dataset.productId === this.dataset.productId);
  }

  #captureBasePrice() {
    const priceTarget = this.#getPriceTarget();
    if (!priceTarget) return;

    this.#basePriceHTML = priceTarget.innerHTML;
    this.#basePriceValue = priceTarget.getAttribute('value') || priceTarget.textContent?.trim() || '';
    this.#baseHasYmqPriceHook = this.#hasYmqPriceHook(priceTarget);
  }

  #schedulePriceForQuantityUpdate(quantity) {
    this.#updatePriceForQuantity(quantity);
    requestAnimationFrame(() => this.#updatePriceForQuantity(quantity));
    setTimeout(() => this.#updatePriceForQuantity(quantity), 75);
    setTimeout(() => this.#updatePriceForQuantity(quantity), 250);
  }

  #updatePriceForQuantity(quantity) {
    const priceTarget = this.#getPriceTarget();
    if (!priceTarget || !this.#baseHasYmqPriceHook) return;

    const tierPrice = this.#getTierPrice(quantity ?? this.#getCurrentQuantity());

    if (!tierPrice) {
      this.#hideTierOverride();
      priceTarget.hidden = false;
      priceTarget.removeAttribute('aria-hidden');
      this.#setTextComponentValue(priceTarget, this.#basePriceValue);
      return;
    }

    priceTarget.hidden = true;
    priceTarget.setAttribute('aria-hidden', 'true');
    this.#showTierOverride(priceTarget, tierPrice);
    this.#setTextComponentValue(priceTarget, tierPrice);
  }

  #showTierOverride(priceTarget, tierPrice) {
    const override = this.#getTierOverride(priceTarget);
    override.textContent = tierPrice;
    override.hidden = false;
  }

  #hideTierOverride() {
    const override = this.querySelector('.product-price__quantity-tier-override');
    if (override instanceof HTMLElement) {
      override.hidden = true;
      override.textContent = '';
    }
  }

  #getTierOverride(priceTarget) {
    const existingOverride = this.querySelector('.product-price__quantity-tier-override');
    if (existingOverride instanceof HTMLElement) return existingOverride;

    const override = document.createElement('span');
    override.className = 'price product-price__quantity-tier-override';
    override.setAttribute('data-ymq-b2b-theme-price-override', '');
    priceTarget.insertAdjacentElement('afterend', override);

    return override;
  }

  #getPriceTarget() {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    const price = priceContainer?.querySelector('.price');

    return price || null;
  }

  #hasYmqPriceHook(priceTarget) {
    return (
      priceTarget.matches('[ymq-b2b-variant-price], .ymq-b2b-current-product-price-parent-dom') ||
      Boolean(priceTarget.querySelector('[ymq-b2b-variant-price], .ymq-b2b-current-product-price'))
    );
  }

  #getCurrentQuantity() {
    const input = this.#section?.querySelector('product-form-component input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return 1;

    return parseInt(input.value, 10) || 1;
  }

  #getTierPrice(quantity) {
    if (!quantity) return '';

    const rows = this.#getTierRows();
    for (const row of rows) {
      const tier = this.#getTierRowData(row);

      if (tier.price && this.#quantityMatchesTier(quantity, tier)) {
        return tier.price;
      }
    }

    return '';
  }

  #getTierRows() {
    const selector = [
      '.bulk-pricing-table__row[data-quantity-range][data-unit-price]',
      '.ymq-b2b-table-tr[ymq-b2b-table-min]',
    ].join(',');
    const sectionRows = this.#filterTierRows([...(this.#section?.querySelectorAll(selector) || [])]);
    if (sectionRows.length) return sectionRows;

    const productContext = this.closest('.product-information, .product, main') || document;
    return this.#filterTierRows([...productContext.querySelectorAll(selector)]);
  }

  #filterTierRows(rows) {
    const productId = this.dataset.productId;
    if (!productId) return rows;

    const matchingRows = rows.filter((row) => {
      const tableProductId = row.closest('[ymq-b2b-table-product-id]')?.getAttribute('ymq-b2b-table-product-id');
      return !tableProductId || tableProductId === productId;
    });

    return matchingRows.length ? matchingRows : rows;
  }

  #getTierRowData(row) {
    if (row.matches('.ymq-b2b-table-tr')) {
      return {
        min: parseInt(row.getAttribute('ymq-b2b-table-min') || '', 10),
        max: parseInt(row.getAttribute('ymq-b2b-table-max') || '', 10),
        price: this.#normalizeTierPrice(row.querySelector('[data-ymq-money], [ymq-b2b-money]')?.textContent || ''),
      };
    }

    return {
      range: row.getAttribute('data-quantity-range') || '',
      price: this.#normalizeTierPrice(row.getAttribute('data-unit-price') || ''),
    };
  }

  #quantityMatchesTier(quantity, tier) {
    if (Number.isFinite(tier.min)) {
      if (quantity < tier.min) return false;
      return !Number.isFinite(tier.max) || quantity <= tier.max;
    }

    return this.#quantityMatchesRange(quantity, tier.range || '');
  }

  #quantityMatchesRange(quantity, range) {
    const normalizedRange = range.replace(/[–—]/g, '-');
    const numbers = normalizedRange.match(/\d+/g)?.map((value) => parseInt(value, 10)) || [];
    if (!numbers.length) return false;

    const [min, max] = numbers;
    if (normalizedRange.includes('+') || numbers.length === 1) return quantity >= min;

    return quantity >= min && quantity <= max;
  }

  #normalizeTierPrice(price) {
    const normalizedPrice = price
      .replace(/\s*\/\s*(each|ea\.?)\s*$/i, '')
      .replace(/\s*(each|ea\.?)\s*$/i, '')
      .trim();

    if (!normalizedPrice || normalizedPrice.includes('%')) return '';

    return normalizedPrice;
  }

  #setTextComponentValue(priceTarget, value) {
    const textComponent = priceTarget.closest('text-component');
    textComponent?.setAttribute('value', value);
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
